import { useState } from "react";
import { Mail, MessageSquare, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import api from "@/lib/api";

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/contact", formData);
      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F] flex flex-col">
      <MarketingHeader />

      <main className="flex-1">
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center fade-in-up">
            <div className="label-mono mb-4">— Get in Touch</div>
            <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-tight">
              We'd love to hear <br />
              <span className="text-[#5C5C66]">from you.</span>
            </h1>
            <p className="mt-6 text-[#5C5C66] text-lg max-w-xl mx-auto leading-relaxed">
              Have a question about pricing, need a custom integration, or just want to say hi? Drop us a message and we'll get back to you within 24 hours.
            </p>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 md:p-10 shadow-soft">
              {status === "success" ? (
                <div className="text-center py-12 animate-in fade-in zoom-in duration-300">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3">Message sent!</h3>
                  <p className="text-[#5C5C66] mb-8">Thanks for reaching out. We'll be in touch shortly.</p>
                  <Button onClick={() => setStatus("idle")} variant="outline" className="rounded-lg">
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 animate-in fade-in duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-[#0A0A0F]">Name</label>
                      <input 
                        required
                        type="text" 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full h-11 px-4 rounded-lg border border-[#EAEAE3] bg-[#FAFAF7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all text-sm"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-[#0A0A0F]">Email address</label>
                      <input 
                        required
                        type="email" 
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full h-11 px-4 rounded-lg border border-[#EAEAE3] bg-[#FAFAF7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all text-sm"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-[#0A0A0F]">Subject</label>
                    <select 
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="w-full h-11 px-4 rounded-lg border border-[#EAEAE3] bg-[#FAFAF7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all text-sm appearance-none"
                    >
                      <option value="" disabled>Select a topic...</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Sales / Pricing">Sales / Pricing</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Partnership">Partnership</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-[#0A0A0F]">Message</label>
                    <textarea 
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full p-4 rounded-lg border border-[#EAEAE3] bg-[#FAFAF7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all text-sm resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>

                  {status === "error" && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <Button type="submit" disabled={status === "loading"} className="w-full h-12 rounded-lg bg-[#0A0A0F] hover:bg-[#1A1A24] text-white font-medium text-base shadow-sm">
                    {status === "loading" ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </div>
            
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 text-center md:text-left">
              <div className="p-6 rounded-2xl bg-white border border-[#EAEAE3] shadow-soft flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-[#002FA7]" />
                </div>
                <div>
                  <h4 className="font-display font-bold text-[#0A0A0F]">Email Support</h4>
                  <p className="text-sm text-[#5C5C66] mt-1">Prefer direct email? Reach us at <a href="mailto:support@leadsb2b.app" className="text-[#0A0A0F] font-medium hover:underline">support@leadsb2b.app</a></p>
                </div>
              </div>
              <div className="p-6 rounded-2xl bg-white border border-[#EAEAE3] shadow-soft flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-5 w-5 text-[#FF5722]" />
                </div>
                <div>
                  <h4 className="font-display font-bold text-[#0A0A0F]">Live Chat</h4>
                  <p className="text-sm text-[#5C5C66] mt-1">Available for Pro & Scale customers inside the dashboard.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
